import { useCallback } from "react";

import { Action, Module, Run, RunType, Stack } from "types/generated";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";

import useHasEntityWriteAccess from "./useHasEntityWriteAccess";
import useHasEntityReadAccess from "./useHasEntityReadAccess";

const RETRY_ACCESS_TYPES = [Action.RunRetry];
const RETRY_BLOCKING_ACCESS_TYPES = [Action.RunRetryBlocking];

const hasRetryAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(RETRY_ACCESS_TYPES, viewer, spaceId);

const hasRetryBlockingAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(RETRY_BLOCKING_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook to determine if the current user has permission to retry a run. Whether the run
 * itself can be retried is decided by shared/Run/getRunActions.
 *
 * For non-blocking runs (e.g. Proposed), users need either:
 * - Stack/Module read access
 * - RunRetry permission
 *
 * For blocking runs (Tracked, Destroy, Task), users need either:
 * - Stack/Module write access
 * - RunRetryBlocking permission
 *
 * @returns A function that takes a run and entity (Stack or Module) and returns an AccessResult
 */
const useCanRetryRun = (): ((
  run: Run,
  entity: Partial<Pick<Stack | Module, "space" | "spaceDetails" | "canWrite">>
) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);
  const hasEntityWriteAccess = useHasEntityWriteAccess();
  const hasEntityReadAccess = useHasEntityReadAccess();

  return useCallback(
    (run: Run, entity: Partial<Pick<Stack | Module, "space" | "spaceDetails" | "canWrite">>) => {
      const hasAccessToRerun =
        hasEntityReadAccess(entity) ||
        hasRetryAccess(viewer, entity.spaceDetails?.id || entity.space);
      const hasAccessToRerunBlocking =
        hasEntityWriteAccess(entity) ||
        hasRetryBlockingAccess(viewer, entity.spaceDetails?.id || entity.space);

      const isBlockingRun =
        run.type === RunType.Tracked || run.type === RunType.Destroy || run.type === RunType.Task;

      const hasAccess = isBlockingRun ? hasAccessToRerunBlocking : hasAccessToRerun;

      if (hasAccess) {
        return {
          result: true,
        };
      }

      return {
        result: false,
        message: NO_ACCESS_MESSAGE,
      };
    },
    [viewer, hasEntityWriteAccess, hasEntityReadAccess]
  );
};

export default useCanRetryRun;
